import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchYoastSEO } from '@/lib/wordpress';
import type { YoastSEO } from '@/types/wordpress';

const SITE_URL = 'https://thefamousgroup.com';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

export interface PageSEOConfig {
  /** WordPress page/post URI for Yoast lookup (e.g. "/company", "/blog/my-post") */
  wpUri?: string;
  /** Fallback title if Yoast data unavailable */
  fallbackTitle: string;
  /** Fallback meta description */
  fallbackDescription: string;
  /** Fallback OG image URL */
  fallbackImage?: string;
  /** Page type for og:type (default: "website") */
  ogType?: string;
  /** Fallback JSON-LD structured data */
  fallbackSchema?: Record<string, unknown>;
  /** Article-specific metadata */
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
  };
}

function setMetaTag(property: string, content: string, isName = false) {
  const attr = isName ? 'name' : 'property';
  let el = document.querySelector(`meta[${attr}="${property}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonicalLink(url: string) {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', url);
}

function removeJsonLd(id: string) {
  const el = document.getElementById(id);
  if (el) el.remove();
}

function injectJsonLd(id: string, data: string | Record<string, unknown>) {
  removeJsonLd(id);
  const script = document.createElement('script');
  script.id = id;
  script.type = 'application/ld+json';
  script.textContent = typeof data === 'string' ? data : JSON.stringify(data);
  document.head.appendChild(script);
}

export function usePageSEO(config: PageSEOConfig) {
  const location = useLocation();
  const previousTitle = useRef(document.title);

  useEffect(() => {
    let cancelled = false;

    const applyDefaults = () => {
      const fullUrl = `${SITE_URL}${location.pathname}`;
      
      document.title = config.fallbackTitle;
      setMetaTag('description', config.fallbackDescription, true);
      setMetaTag('og:title', config.fallbackTitle);
      setMetaTag('og:description', config.fallbackDescription);
      setMetaTag('og:image', config.fallbackImage || DEFAULT_OG_IMAGE);
      setMetaTag('og:url', fullUrl);
      setMetaTag('og:type', config.ogType || 'website');
      setMetaTag('twitter:card', 'summary_large_image');
      setMetaTag('twitter:title', config.fallbackTitle);
      setMetaTag('twitter:description', config.fallbackDescription);
      setMetaTag('twitter:image', config.fallbackImage || DEFAULT_OG_IMAGE);
      setCanonicalLink(fullUrl);
      setMetaTag('robots', 'index, follow', true);

      if (config.article) {
        if (config.article.publishedTime) setMetaTag('article:published_time', config.article.publishedTime);
        if (config.article.modifiedTime) setMetaTag('article:modified_time', config.article.modifiedTime);
        if (config.article.author) setMetaTag('article:author', config.article.author);
        if (config.article.section) setMetaTag('article:section', config.article.section);
      }

      if (config.fallbackSchema) {
        injectJsonLd('page-schema', config.fallbackSchema);
      }
    };

    const applyYoast = (seo: YoastSEO) => {
      const fullUrl = `${SITE_URL}${location.pathname}`;

      document.title = seo.title || config.fallbackTitle;
      setMetaTag('description', seo.metaDesc || config.fallbackDescription, true);
      setMetaTag('og:title', seo.opengraphTitle || seo.title || config.fallbackTitle);
      setMetaTag('og:description', seo.opengraphDescription || seo.metaDesc || config.fallbackDescription);
      setMetaTag('og:image', seo.opengraphImage?.sourceUrl || config.fallbackImage || DEFAULT_OG_IMAGE);
      setMetaTag('og:url', seo.opengraphUrl || fullUrl);
      setMetaTag('og:type', config.ogType || 'website');
      setMetaTag('twitter:card', 'summary_large_image');
      setMetaTag('twitter:title', seo.twitterTitle || seo.opengraphTitle || seo.title || config.fallbackTitle);
      setMetaTag('twitter:description', seo.twitterDescription || seo.opengraphDescription || seo.metaDesc || config.fallbackDescription);
      setMetaTag('twitter:image', seo.twitterImage?.sourceUrl || seo.opengraphImage?.sourceUrl || config.fallbackImage || DEFAULT_OG_IMAGE);
      // Always use production canonical — never trust CMS canonical
      setCanonicalLink(fullUrl);

      // Always enforce indexable — ignore CMS noindex directives
      setMetaTag('robots', 'index, follow', true);

      // Schema / JSON-LD
      if (seo.schema?.raw) {
        injectJsonLd('page-schema', seo.schema.raw);
      } else if (config.fallbackSchema) {
        injectJsonLd('page-schema', config.fallbackSchema);
      }
    };

    // Apply defaults immediately
    applyDefaults();

    // Then try to fetch Yoast data
    if (config.wpUri) {
      fetchYoastSEO(config.wpUri).then((seo) => {
        if (!cancelled && seo) {
          applyYoast(seo);
        }
      }).catch(() => {
        // Fallbacks already applied
      });
    }

    return () => {
      cancelled = true;
      removeJsonLd('page-schema');
    };
  }, [location.pathname, config.wpUri, config.fallbackTitle, config.fallbackDescription]);
}
